import {FullWorkout, IndoorFullWorkout, OutdoorFullWorkout, PartType} from '../Models/FullWorkout';
import {Distance} from '../Domain/Distance';
import {Duration} from '../Domain/Duration';
import {lazy} from '../Support/lazy';
import {Speed} from '../Domain/Speed';
import {StrokeRate} from '../Domain/StrokeRate';
import {unrollParts} from './unrollParts';
import {Energy} from '../Domain/Energy';
import {WorkoutExecutionType} from '../Models/WorkoutExecutionType';
import {Power} from '../Domain/Power';
import {Fraction} from '../Domain/Fraction';
import {Force} from '../Domain/Force';

export abstract class FullWorkoutStatisticProvider {
    public static create(fullWorkout: FullWorkout): FullWorkoutStatisticProvider {
        switch (fullWorkout.execution_type) {
            case WorkoutExecutionType.INDOOR:
                return new IndoorFullWorkoutStatisticProvider(fullWorkout as IndoorFullWorkout);
            case WorkoutExecutionType.OUTDOOR:
                return new OutdoorFullWorkoutStatisticProvider(fullWorkout as OutdoorFullWorkout);
        }
    }

    public abstract strokeCount: number | null;
    public abstract totalDistance: Distance;
    public abstract totalDuration: Duration;
    public abstract averageSpeed: Speed;
    public abstract strokeRate: StrokeRate | null;
    public abstract averagePower: Power | null;
    public abstract relativePeakForcePosition: Fraction | null;
    public abstract averagePeakForce: Force | null;
    public abstract averageStrokeLength: Distance | null;
    public abstract activeStrokeCount: number | null;
    public abstract activeDistance: Distance;
    public abstract activeDuration: Duration;
    public abstract activeAverageSpeed: Speed;
    public abstract activeStokeRate: StrokeRate | null;
    public abstract burnedCalories: Energy | null;
    public abstract averageDriveTime: Duration | null;
    public abstract averageRecoverTime: Duration | null;
    public abstract averageEnergyPerStroke: Energy | null;
}

class OutdoorFullWorkoutStatisticProvider extends FullWorkoutStatisticProvider {
    constructor(private outdoorFullWorkout: OutdoorFullWorkout) {
        super();
    }

    @lazy()
    private get activeWorkoutPartResults() {
        const unrolledParts = unrollParts(this.outdoorFullWorkout);
        if (this.outdoorFullWorkout.training_plan === null || unrolledParts === null) {
            return this.outdoorFullWorkout.workout_part_results;
        }

        return this.outdoorFullWorkout.workout_part_results.filter((workoutPartResult, key) => {
            const unrolledPart = unrolledParts[key];

            return unrolledPart !== undefined && unrolledPart.type !== PartType.Rest;
        });
    }

    @lazy()
    public get strokeCount(): number | null {
        return null;
    }

    @lazy()
    public get totalDistance(): Distance {
        return this.outdoorFullWorkout.workout_part_results.reduce(
            (sumWorkout, result) => sumWorkout.add(result.traveled_distance),
            Distance.fromMeters(0),
        );
    }

    @lazy()
    public get totalDuration(): Duration {
        return this.outdoorFullWorkout.workout_part_results.reduce(
            (sumWorkout, result) => sumWorkout.add(result.elapsed_time),
            Duration.fromMilliseconds(0),
        );
    }

    @lazy()
    public get averageSpeed(): Speed {
        return Speed.fromDistanceAndDuration(this.totalDistance, this.totalDuration);
    }

    @lazy()
    public get strokeRate(): StrokeRate | null {
        return null;
    }

    @lazy()
    public get averagePower(): Power | null {
        return null;
    }

    @lazy()
    public get relativePeakForcePosition(): Fraction | null {
        return null;
    }

    @lazy()
    public get averagePeakForce(): Force | null {
        return null;
    }

    @lazy()
    public get averageStrokeLength(): Distance | null {
        return null;
    }

    @lazy()
    public get activeStrokeCount(): number | null {
        return null;
    }

    @lazy()
    public get activeDistance(): Distance {
        return this.totalDistance;
    }

    @lazy()
    public get activeDuration(): Duration {
        return this.totalDuration;
    }

    @lazy()
    public get activeAverageSpeed(): Speed {
        return this.averageSpeed;
    }

    @lazy()
    public get activeStokeRate(): StrokeRate | null {
        return null;
    }

    @lazy()
    public get burnedCalories(): Energy | null {
        return null;
    }

    @lazy()
    public get averageDriveTime(): Duration | null {
        return null;
    }

    @lazy()
    public get averageRecoverTime(): Duration | null {
        return null;
    }

    @lazy()
    public get averageEnergyPerStroke(): Energy | null {
        return null;
    }
}

class IndoorFullWorkoutStatisticProvider extends FullWorkoutStatisticProvider {
    constructor(private indoorFullWorkout: IndoorFullWorkout) {
        super();
    }

    @lazy()
    private get activeWorkoutPartResults() {
        const unrolledParts = unrollParts(this.indoorFullWorkout);
        if (this.indoorFullWorkout.training_plan === null || unrolledParts === null) {
            return this.indoorFullWorkout.workout_part_results;
        }

        return this.indoorFullWorkout.workout_part_results.filter((workoutPartResult, key) => {
            const unrolledPart = unrolledParts[key];

            return unrolledPart !== undefined && unrolledPart.type !== PartType.Rest;
        });
    }

    @lazy()
    public get strokeCount(): number {
        return this.indoorFullWorkout.workout_part_results.reduce(
            (sumWorkout, result) => sumWorkout + result.stroke_count,
            0,
        );
    }

    @lazy()
    public get totalDistance(): Distance {
        return this.indoorFullWorkout.workout_part_results.reduce(
            (sumWorkout, result) => sumWorkout.add(result.traveled_distance),
            Distance.fromMeters(0),
        );
    }

    @lazy()
    public get totalDuration(): Duration {
        return this.indoorFullWorkout.workout_part_results.reduce(
            (sumWorkout, result) => sumWorkout.add(result.elapsed_time),
            Duration.fromMilliseconds(0),
        );
    }

    @lazy()
    public get averageSpeed(): Speed {
        return Speed.fromDistanceAndDuration(this.totalDistance, this.totalDuration);
    }

    @lazy()
    public get strokeRate(): StrokeRate | null {
        return StrokeRate.fromStrokeCountAndDuration(this.strokeCount, this.totalDuration);
    }

    @lazy()
    public get averagePower(): Power | null {
        const sum = this.indoorFullWorkout.workout_part_results.reduce((p: number | null, c) => {
            if (c.average_power === null) {
                return p;
            }

            if (p === null) {
                return c.average_power.watts * c.elapsed_time.milliseconds;
            }

            //Weighted average power
            return p + c.average_power.watts * c.elapsed_time.milliseconds;
        }, null);
        return sum === null ? null : Power.fromWatts(sum / this.totalDuration.milliseconds);
    }

    @lazy()
    public get relativePeakForcePosition(): Fraction | null {
        const sum = this.indoorFullWorkout.workout_part_results.reduce((p: number | null, c) => {
            if (c.average_relative_peak_force_position === null) {
                return p;
            }

            if (p === null) {
                return c.average_relative_peak_force_position.value * c.elapsed_time.milliseconds;
            }

            //Weighted average relative peak force position
            return p + c.average_relative_peak_force_position.value * c.elapsed_time.milliseconds;
        }, null);

        return sum === null ? null : Fraction.fromValue(sum / this.totalDuration.milliseconds);
    }

    @lazy()
    public get averagePeakForce(): Force | null {
        const sum = this.indoorFullWorkout.workout_part_results.reduce((p: number | null, c) => {
            if (c.average_peak_force === null) {
                return p;
            }

            if (p === null) {
                return c.average_peak_force.newton * c.elapsed_time.milliseconds;
            }

            //Weighted average peak force
            return p + c.average_peak_force.newton * c.elapsed_time.milliseconds;
        }, null);

        return sum === null ? null : Force.fromNewton(sum / this.totalDuration.milliseconds);
    }

    @lazy()
    public get averageStrokeLength(): Distance | null {
        const sum = this.indoorFullWorkout.workout_part_results.reduce((p: number | null, c) => {
            if (c.average_stroke_length === null) {
                return p;
            }

            if (p === null) {
                return c.average_stroke_length.millimeters * c.elapsed_time.milliseconds;
            }

            //Weighted average stroke length
            return p + c.average_stroke_length.millimeters * c.elapsed_time.milliseconds;
        }, null);

        return sum === null ? null : Distance.fromMillimeters(sum / this.totalDuration.milliseconds);
    }

    @lazy()
    public get activeStrokeCount(): number {
        return this.activeWorkoutPartResults.reduce((sumWorkout, result) => sumWorkout + result.stroke_count, 0);
    }

    @lazy()
    public get activeDistance(): Distance {
        return this.activeWorkoutPartResults.reduce(
            (sumWorkout, result) => sumWorkout.add(result.traveled_distance),
            Distance.fromMeters(0),
        );
    }

    @lazy()
    public get activeDuration(): Duration {
        return this.activeWorkoutPartResults.reduce(
            (sumWorkout, result) => sumWorkout.add(result.elapsed_time),
            Duration.fromMilliseconds(0),
        );
    }

    @lazy()
    public get activeAverageSpeed(): Speed {
        return Speed.fromDistanceAndDuration(this.activeDistance, this.activeDuration);
    }

    @lazy()
    public get activeStokeRate(): StrokeRate | null {
        return StrokeRate.fromStrokeCountAndDuration(this.activeStrokeCount, this.activeDuration);
    }

    @lazy()
    public get burnedCalories(): Energy | null {
        return this.indoorFullWorkout.workout_part_results.reduce((p: Energy | null, c) => {
            if (c.burned_calories === null) {
                return p;
            }

            if (p === null) {
                return c.burned_calories;
            }

            return p.add(c.burned_calories);
        }, null);
    }

    @lazy()
    public get averageDriveTime(): Duration | null {
        const sum = this.indoorFullWorkout.workout_part_results.reduce((p: number | null, c) => {
            if (c.average_drive_time === null) {
                return p;
            }

            if (p === null) {
                return c.average_drive_time.milliseconds * c.elapsed_time.milliseconds;
            }

            //Weighted average drive time
            return p + c.average_drive_time.milliseconds * c.elapsed_time.milliseconds;
        }, null);

        return sum === null ? null : Duration.fromMilliseconds(sum / this.totalDuration.milliseconds);
    }

    @lazy()
    public get averageRecoverTime(): Duration | null {
        const sum = this.indoorFullWorkout.workout_part_results.reduce((p: number | null, c) => {
            if (c.average_recover_time === null) {
                return p;
            }

            if (p === null) {
                return c.average_recover_time.milliseconds * c.elapsed_time.milliseconds;
            }

            //Weighted average recover time
            return p + c.average_recover_time.milliseconds * c.elapsed_time.milliseconds;
        }, null);

        return sum === null ? null : Duration.fromMilliseconds(sum / this.totalDuration.milliseconds);
    }

    @lazy()
    public get averageEnergyPerStroke(): Energy | null {
        const sum = this.indoorFullWorkout.workout_part_results.reduce((p: number | null, c) => {
            if (c.average_energy_per_stroke === null) {
                return p;
            }

            if (p === null) {
                return c.average_energy_per_stroke.joules * c.elapsed_time.milliseconds;
            }

            //Weighted average energy per stroke
            return p + c.average_energy_per_stroke.joules * c.elapsed_time.milliseconds;
        }, null);

        return sum === null ? null : Energy.fromJoules(sum / this.totalDuration.milliseconds);
    }
}
